import type { MonitoringInvestigationContext } from "../../../shared/chatInvestigation";
import { AskMonitoringChat } from "./AskMonitoringChat";
import { useMemo } from "react";
import { queries } from "../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { Text } from "../Text";
import { AiProviderSpendDonut } from "./AiProviderSpendDonut";
import { computeAiProviderAllocationRows } from "./aiProviderMonitoring";
import { type AiSpendRecord, formatEstimatedCostUsd } from "./aiUsage";
import { spaceColorMap, spaceTrackColorMap } from "./monitoringPanelLayout";
import { InfoCorner, PanelBadge } from "./PanelInfo";
import { Panel, PanelHeader, TotalRow } from "./workflowsLayout";

type Props = {
  investigation: MonitoringInvestigationContext;
  loading: boolean;
  providerId: string | null;
  limitAmount: number | null;
  spaceBudgets: readonly {
    id: string;
    scopeId: string;
    limitAmount: number | null;
  }[];
  spendRecords: readonly AiSpendRecord[];
};

export function AiSpendVsLimitsPanel({
  providerId,
  limitAmount,
  spaceBudgets,
  spendRecords,
  investigation,
  loading,
}: Props) {
  const [allSpaces] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );
  const displayName = useSpaceDisplayName();
  const teamSpaces = useMemo(
    () =>
      (allSpaces ?? [])
        .filter((space) => space.mode !== "personal")
        .map((space) => ({ id: space.id, name: displayName(space) })),
    [allSpaces, displayName]
  );
  const colorMap = useMemo(() => spaceColorMap(allSpaces ?? []), [allSpaces]);
  const trackColorMap = useMemo(
    () => spaceTrackColorMap(allSpaces ?? []),
    [allSpaces]
  );
  const spendUsd = useMemo(
    () => spendRecords.reduce((sum, record) => sum + (record.spendUsd ?? 0), 0),
    [spendRecords]
  );
  const hasUnpricedUsage = spendRecords.some(
    (record) => record.spendUsd == null
  );
  const allocationRows = useMemo(
    () =>
      computeAiProviderAllocationRows(
        providerId,
        spendRecords,
        teamSpaces,
        spaceBudgets,
        limitAmount,
        spendUsd
      ),
    [providerId, spendRecords, teamSpaces, spaceBudgets, limitAmount, spendUsd]
  );

  return (
    <Panel>
      <InfoCorner>
        <PanelBadge>Rolling 30 days</PanelBadge>
      </InfoCorner>
      <PanelHeader>
        <Text variant="text-small-md">Spend vs limits</Text>
        <TotalRow>
          <Text variant="display-md">
            {formatEstimatedCostUsd(spendUsd ?? 0)}
          </Text>
        </TotalRow>
        {hasUnpricedUsage && (
          <Text variant="text-small" color="var(--text-secondary)">
            Some usage has no configured price
          </Text>
        )}
      </PanelHeader>
      <AiProviderSpendDonut
        rows={allocationRows}
        colorMap={colorMap}
        trackColorMap={trackColorMap}
      />
      <AskMonitoringChat
        disabled={loading}
        surface="ai-limits"
        context={investigation}
        snapshot={JSON.stringify({
          spendUsd,
          limitUsd: limitAmount,
          hasUnpricedUsage,
          displayedAllocationCount: allocationRows.length,
          leadingAllocations: allocationRows.slice(0, 10).map((row) => ({
            id: row.id,
            name: row.label.slice(0, 100),
            spendUsd: row.spendUsd,
            limitUsd: row.limitAmount,
          })),
        })}
      />
    </Panel>
  );
}
